import { supabase } from '../services/supabaseClient';
import { logAuditEvent } from '../services/AuditService';
import { clearSupabaseSession, clearSessionMetadata } from './sessionCleanup';

export type LogoutReason = 'manual' | 'inactivity' | 'other';
export type LogoutScope = 'global' | 'local';

// Tope de espera del insert de auditoría: el logout no debe bloquearse si la red va lenta.
const AUDIT_TIMEOUT_MS = 2000;

/**
 * Cierra sesión registrando ANTES el evento 'logout' en audit_events.
 *
 * El evento tiene que escribirse mientras el JWT sigue vigente: una vez que
 * signOut() borra la sesión, el insert sale como anon y la policy
 * audit_events_insert_own (TO authenticated, firm_id = auth.uid()) lo rechaza.
 *
 * scope 'global' (default de supabase-js) revoca todas las sesiones del usuario;
 * 'local' solo la de este dispositivo (igualmente hace POST /logout?scope=local,
 * así que revoca esa sesión en el servidor).
 *
 * Si el POST /logout falla (p. ej. sin red), supabase-js devuelve el error y NO
 * borra la sesión local: en un equipo compartido el usuario quedaría logueado.
 * En ese caso se fuerza la limpieza local y se redirige a '/'. La sesión del
 * servidor sigue viva hasta que expire, pero este navegador ya no la tiene.
 */
export async function signOutWithAudit(reason: LogoutReason, scope: LogoutScope = 'global') {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const uid = session?.user?.id;
    if (uid) {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<void>((resolve) => { timer = setTimeout(resolve, AUDIT_TIMEOUT_MS); });
      await Promise.race([
        logAuditEvent(supabase, uid, uid, {
          action:      'logout',
          entityType:  'session',
          entityId:    uid,
          changesJson: { reason, scope, timestamp: new Date().toISOString() },
        }),
        timeout,
      ]);
      clearTimeout(timer);
    }
  } catch (err) {
    console.warn('[signOutWithAudit] audit before signOut failed — continuing with signOut:', err);
  }
  const result = await supabase.auth.signOut({ scope });
  if (result.error) {
    console.warn('[signOutWithAudit] signOut falló — forzando limpieza local de la sesión:', result.error.message);
    clearSupabaseSession();
    clearSessionMetadata();
    if (typeof window !== 'undefined') window.location.assign('/');
  }
  return result;
}
